import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Check, Trophy, Search, RefreshCw, Loader2 } from 'lucide-react';

const API = window.location.origin || '';

export default function WordSearch() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [puzzle, setPuzzle] = useState(null);
  const [foundWords, setFoundWords] = useState([]);
  const [foundWordPositions, setFoundWordPositions] = useState([]); // Store positions of found words
  const [loading, setLoading] = useState(true);
  
  // Selection state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  
  // Load puzzle immediately on mount
  useEffect(() => {
    if (user) {
      loadInstantPuzzle();
    }
  }, [user]);
  
  const loadInstantPuzzle = async () => {
    setLoading(true);
    setFoundWords([]);
    setFoundWordPositions([]);
    try {
      const res = await fetch(`${API}/api/crossword/instant`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPuzzle(data);
        setFoundWords(data.found_words || []);
        // If there are already found words, get their positions
        if (data.found_word_positions) {
          setFoundWordPositions(data.found_word_positions);
        }
      } else {
        toast.error('Failed to load puzzle');
      }
    } catch (error) {
      console.error('Error loading puzzle:', error);
      toast.error('Failed to load puzzle');
    } finally {
      setLoading(false);
    }
  };
  
  // Get cells in a line between two points
  const getCellsBetween = (start, end) => {
    if (!start || !end) return [];
    
    const cells = [];
    const rowDiff = end.row - start.row;
    const colDiff = end.col - start.col;
    
    // Calculate direction
    const steps = Math.max(Math.abs(rowDiff), Math.abs(colDiff));
    if (steps === 0) return [[start.row, start.col]];
    
    const rowStep = rowDiff / steps;
    const colStep = colDiff / steps;
    
    // Only allow straight lines (horizontal, vertical, diagonal)
    if (rowStep !== 0 && colStep !== 0 && Math.abs(rowStep) !== Math.abs(colStep)) {
      return [];
    }
    
    for (let i = 0; i <= steps; i++) {
      const row = Math.round(start.row + i * rowStep);
      const col = Math.round(start.col + i * colStep);
      cells.push([row, col]);
    }
    
    return cells;
  };
  
  const handleMouseDown = (row, col) => {
    setIsSelecting(true);
    setSelectionStart({ row, col });
    setSelectedCells([[row, col]]);
  };
  
  const handleMouseEnter = (row, col) => {
    if (isSelecting && selectionStart) {
      const cells = getCellsBetween(selectionStart, { row, col });
      setSelectedCells(cells);
    }
  };
  
  const handleMouseUp = async () => {
    if (!isSelecting || selectedCells.length < 2) {
      setIsSelecting(false);
      setSelectedCells([]);
      setSelectionStart(null);
      return;
    }
    
    setIsSelecting(false);
    
    // Check if word is valid
    try {
      const res = await fetch(`${API}/api/crossword/check-word`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          puzzle_id: puzzle.puzzle_id,
          is_daily: false,
          selected_cells: selectedCells
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          toast.success(`Found: ${data.word}!`);
          setFoundWords(prev => [...prev, data.word]);
          // Add the positions of the found word
          if (data.positions) {
            setFoundWordPositions(prev => [...prev, ...data.positions.map(p => `${p[0]}-${p[1]}`)]);
          }
          
          if (data.completed) {
            toast.success('🎉 Puzzle complete! All 20 words found!');
          }
        }
      }
    } catch (error) {
      console.error('Error checking word:', error);
    }
    
    setSelectedCells([]);
    setSelectionStart(null);
  };
  
  // Check if cell is part of a found word
  const isCellFound = (row, col) => {
    return foundWordPositions.includes(`${row}-${col}`);
  };
  
  // Check if cell is currently selected
  const isCellSelected = (row, col) => {
    return selectedCells.some(([r, c]) => r === row && c === col);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#0047AB] mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Loading Word Search...</p>
        </div>
      </div>
    );
  }
  
  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Failed to Load Puzzle</h2>
            <p className="text-slate-500 mb-6">Please try again</p>
            <Button onClick={loadInstantPuzzle} className="bg-[#0047AB] hover:bg-[#003380]">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const gridSize = puzzle.grid_size || 15;
  const allWordsFound = foundWords.length >= puzzle.total_words;
  
  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4"
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        if (isSelecting) {
          setIsSelecting(false);
          setSelectedCells([]);
          setSelectionStart(null);
        }
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-[#0047AB] dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          
          <Button 
            onClick={loadInstantPuzzle}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            New Puzzle
          </Button>
        </div>
        
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <Search className="w-7 h-7 text-[#0047AB]" />
              Word Search Challenge
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Find all 20 hidden certification terms!
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-3xl font-bold text-[#0047AB]">
              {foundWords.length} / {puzzle.total_words}
            </div>
            <div className="text-sm text-slate-500">Words Found</div>
          </div>
        </div>
        
        {/* Completion Banner */}
        {allWordsFound && (
          <Card className="mb-6 bg-gradient-to-r from-[#0047AB] to-[#003380] border-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 text-white">
                <Trophy className="w-12 h-12" />
                <div>
                  <div className="font-bold text-2xl">
                    🎉 Congratulations!
                  </div>
                  <div className="text-blue-100">
                    You found all {puzzle.total_words} words! Click "New Puzzle" to play again.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Grid */}
          <div className="lg:col-span-2">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
              <CardContent className="p-4">
                <div 
                  className="inline-block bg-[#0047AB] p-1.5 rounded-lg select-none w-full"
                  style={{ touchAction: 'none' }}
                >
                  <div 
                    className="grid gap-0.5"
                    style={{ 
                      gridTemplateColumns: `repeat(${gridSize}, 1fr)`
                    }}
                  >
                    {puzzle.grid.map((row, rowIdx) =>
                      row.map((letter, colIdx) => {
                        const isSelected = isCellSelected(rowIdx, colIdx);
                        const isFound = isCellFound(rowIdx, colIdx);
                        
                        return (
                          <div
                            key={`${rowIdx}-${colIdx}`}
                            onMouseDown={() => handleMouseDown(rowIdx, colIdx)}
                            onMouseEnter={() => handleMouseEnter(rowIdx, colIdx)}
                            className={`
                              aspect-square flex items-center justify-center 
                              text-xs sm:text-sm md:text-base font-bold cursor-pointer
                              transition-all select-none rounded-sm
                              ${isSelected 
                                ? 'bg-[#0047AB] text-white scale-110 z-10' 
                                : isFound
                                  ? 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-400'
                                  : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-blue-50 dark:hover:bg-slate-700'
                              }
                            `}
                          >
                            {letter}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 text-center">
                  Click and drag to select words • Words can go in any direction, including backwards!
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Words to Find */}
          <div>
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 shadow-lg sticky top-4">
              <CardHeader className="py-4 border-b border-slate-200 dark:border-slate-700">
                <CardTitle className="text-lg text-slate-900 dark:text-white flex items-center justify-between">
                  <span>Words to Find</span>
                  <span className="text-sm font-normal text-slate-500">
                    {foundWords.length}/{puzzle.total_words}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {puzzle.words_to_find?.map((wordData, idx) => {
                    const isFound = foundWords.includes(wordData.word);
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-2 rounded-lg transition-all text-center ${
                          isFound 
                            ? 'bg-[#0047AB]/10 dark:bg-[#0047AB]/20 border border-[#0047AB]/30' 
                            : 'bg-slate-50 dark:bg-slate-700/30 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          {isFound && <Check className="w-3.5 h-3.5 text-[#0047AB]" />}
                          <span className={`font-mono text-sm font-bold ${
                            isFound 
                              ? 'text-[#0047AB] line-through opacity-60' 
                              : 'text-slate-800 dark:text-slate-200'
                          }`}>
                            {wordData.word}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* How to Play */}
            <Card className="mt-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 text-sm">How to Play</h4>
                <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                  <li>• Click and drag to select letters</li>
                  <li>• Words can be horizontal, vertical, or diagonal</li>
                  <li>• Words can be spelled backwards!</li>
                  <li>• Found words turn gray in the grid</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
